import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getSafeLocalStorage,
  safeGetItem,
  safeGetJSON,
  safeRemoveItem,
  safeSetItem,
} from "../utils/storage";

function normalizeRole(role) {
  if (role === "consultancy" || role === "consultancy_owner") return "owner";
  return role;
}

function readStoredUser() {
  const parsed = safeGetJSON("user");
  if (parsed?.role) {
    parsed.role = normalizeRole(parsed.role);
  }
  return parsed;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [token, setToken] = useState(() => safeGetItem("token"));

  const login = (nextUser, nextToken) => {
    if (!nextToken) return;
    safeSetItem("token", nextToken);
    setToken(nextToken);

    if (nextUser) {
      const normalizedUser = {
        ...nextUser,
        role: normalizeRole(nextUser.role),
      };
      safeSetItem("user", JSON.stringify(normalizedUser));
      setUser(normalizedUser);
    }
  };

  const logout = () => {
    safeRemoveItem("token");
    safeRemoveItem("user");
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    const storage = getSafeLocalStorage();
    if (!storage || typeof window === "undefined") return;

    const handleStorage = (event) => {
      if (event.key === "token") {
        setToken(event.newValue);
        if (!event.newValue) {
          setUser(null);
        }
      }
      if (event.key === "user") {
        if (event.newValue) {
          try {
            const parsed = JSON.parse(event.newValue);
            parsed.role = normalizeRole(parsed.role);
            setUser(parsed);
          } catch {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token),
      login,
      logout,
    }),
    [user, token]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
